/**
 * ThinkingTimeline: the AI's reasoning for one run, a centre-aligned collapsible activity
 * marker just before the AI reply. Live (spinner, auto-expanded) while generating, then
 * collapsed to a one-line summary. The delta from the previous step is the time it took.
 */

import { useState } from 'react';
import type { LucideIcon } from 'lucide-react';
import { Activity, Brain, Search, Wrench, ShieldCheck, Sparkles, Loader2 } from 'lucide-react';
import type { ThinkingStep } from '@/stores/websocket.store';
import { StepIO } from '../ui/step-io';
import { ThreadDisclosure } from '../ui/thread-disclosure';
import { StepRail, type RailStep } from '../ui/step-rail';
import { ToolProvenance } from '../ui/tool-provenance';

// A step's icon and tone by node kind. node_id prefixes are the stable signal across V3
// (enrichment / policy_tool_call_* / react_tool_call_* / decision_quality_check).
const kindOf = (step: ThinkingStep): { icon: LucideIcon; tone: string } => {
  const id = step.nodeId || '';
  if (id.startsWith('policy_tool')) return { icon: Search, tone: 'text-blue-500' };
  // Any provider call reads as a tool. Enrichment and the ReAct loop both make
  // them, and which phase ran it is not what the reader is looking for.
  if (step.toolName || id.startsWith('react_tool')) return { icon: Wrench, tone: 'text-amber-500' };
  if (id === 'enrichment' || id.includes('enrich')) return { icon: Brain, tone: 'text-violet-500' };
  if (id === 'decision_quality_check' || id.includes('decision')) return { icon: ShieldCheck, tone: 'text-emerald-500' };
  return { icon: Sparkles, tone: 'text-indigo-400' };
};

const fmtDuration = (ms: number): string => (ms < 1000 ? `${ms}ms` : `${(ms / 1000).toFixed(1)}s`);

const fmtArgs = (args: Record<string, unknown>): string => {
  const summary = Object.entries(args)
    .map(([k, v]) => `${k}: ${typeof v === 'string' ? v : JSON.stringify(v)}`)
    .join(', ');
  return summary.length > 80 ? `${summary.slice(0, 79)}…` : summary;
};

const StepDetail = ({ step }: { step: ThinkingStep }) => {
  const items = step.resultItems ?? [];
  return (
    <>
      {!step.done && step.toolArgs && Object.keys(step.toolArgs).length > 0 && (
        <p className='mt-0.5 break-all font-mono text-[10px] leading-snug text-muted-foreground'>
          {fmtArgs(step.toolArgs)}
        </p>
      )}
      {step.resultSummary && (
        <p className='mt-0.5 text-[10px] leading-snug wrap-break-word text-muted-foreground'>{step.resultSummary}</p>
      )}
      {items.length > 0 && (
        <p className='mt-0.5 text-[10px] leading-snug text-muted-foreground'>
          {items
            .slice(0, 3)
            .map((it) => `“${it}”`)
            .join(', ')}
          {items.length > 3 ? ` +${items.length - 3} more` : ''}
        </p>
      )}
      {step.done && (
        <StepIO name={step.toolName ?? step.label} input={step.toolArgs} output={step.toolOutput} simulated={step.simulated} />
      )}
    </>
  );
};

const railSteps = (steps: ThinkingStep[], live: boolean): RailStep[] =>
  steps.map((step, i) => {
    const { icon, tone } = kindOf(step);
    const errored = step.outcome === 'error';
    const running = live && !step.done;
    const delta = i > 0 ? step.timestamp - steps[i - 1].timestamp : 0;
    return {
      key: `${step.nodeId}-${i}`,
      icon,
      tone: errored ? 'text-red-500' : tone,
      running,
      label: (
        <>
          {step.label}
          {running && '…'}
          {(step.simulated || step.grounded || step.simulatedWrite) && (
            <ToolProvenance
              simulated={step.simulated}
              grounded={step.grounded}
              simulatedWrite={step.simulatedWrite}
              gated={step.isWrite}
            />
          )}
          {step.outcome === 'awaiting_approval' && (
            <span className='ml-1.5 text-[10px] text-amber-600'>· awaiting approval</span>
          )}
          {errored && <span className='ml-1.5 text-[10px] text-red-500'>· failed</span>}
        </>
      ),
      meta:
        step.durationMs !== undefined ? fmtDuration(step.durationMs) : i > 0 ? `+${fmtDuration(delta)}` : undefined,
      detail: <StepDetail step={step} />,
    };
  });

interface ThinkingTimelineProps {
  steps: ThinkingStep[];
  live: boolean;
}

export const ThinkingTimeline = ({ steps, live }: ThinkingTimelineProps) => {
  // Force-open while live; after the run, default collapsed but user-toggleable.
  const [userOpen, setUserOpen] = useState(false);
  const open = live || userOpen;

  if (!steps.length) return null;

  const total = steps.length > 1 ? steps[steps.length - 1].timestamp - steps[0].timestamp : 0;

  return (
    <ThreadDisclosure
      icon={live ? Loader2 : Activity}
      iconClassName={live ? 'animate-spin text-indigo-500' : undefined}
      label={live ? 'Thinking…' : 'AI reasoning'}
      meta={
        <span className='opacity-70'>
          {steps.length} step{steps.length === 1 ? '' : 's'}
          {/* A running timer only while live; once done, each step carries its own duration. */}
          {live && total > 0 && ` · ${fmtDuration(total)}`}
        </span>
      }
      active={live}
      open={open}
      onToggle={() => setUserOpen((o) => !o)}
      contentClassName='max-w-[480px]'
    >
      <div className='mt-2 rounded-xl border bg-muted/30 px-3.5 py-3'>
        <StepRail steps={railSteps(steps, live)} />
      </div>
    </ThreadDisclosure>
  );
};
